<template>
  <Dialog
    v-model:visible="visible"
    modal
    @hide="handleHideSurveyModal"
    :pt="{
      root: {
        class: 'survey-modal-root'
      },
    }"
  >
    <template #container="{ closeCallback }">
      <div
        class="survey-modal-container"
        :style="{
          background:
          (currentStep?.type === 'welcome' && !isDesktop)
              ? `linear-gradient(180deg,rgba(171, 183, 246, 0.5) 0%,rgba(208, 203, 255, 0) 100%)`
              : `#FFFFFF`,
        }"
      >
        <!-- Header -->
        <div class="survey-header">
          <div class="survey-header-top">
            <div class="survey-header-left">
              <custom-button
              v-if="currentStep?.type !== 'thank_you'"
                @click="closeCallback"
                class="save-exit-button"
                severity="info"
              >
                <i class="pi pi-chevron-left"></i>
                {{ currentStep?.type === 'welcome' ? "Назад" : "Сохранить и выйти" }}
              </custom-button>
            </div>

            <div class="survey-header-center">
              <div v-if="currentStep?.type === 'welcome'" class="survey-title title">
                {{ surveyStore.surveyModal.config?.title || 'Мои Размеры' }}
              </div>
            </div>

            <div class="survey-header-right">
              <custom-button
                  @click="closeCallback"
                  class="save-exit-button-right "
                  severity="info"
                >
                  {{ isWelcomeStep ? "" : " " }}
                  <i class="pi pi-times"></i>
                </custom-button>
            </div>
          </div>
          <div class="survey-header-bottom">
            <div v-if="currentStep && currentStep.type !== 'welcome'" class="progress-container">
              <div v-if="currentStep?.type !== 'thank_you'" class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${progressPercentage}%` }"
                ></div>
              </div>
              <span  v-if="currentStep?.type !== 'thank_you'" class="progress-text helper-text">{{ surveyModal.currentStepIndex + 1 }} из {{ totalSteps }}</span>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="survey-content">
          <DynamicStepRenderer v-if="!surveyModal.isLoadingConfig" />
          <div v-else class="loading-container">
            <div class="loading-spinner"></div>
            <p>Загрузка конфигурации...</p>
          </div>
        </div>

        <!-- Footer -->
        <div 
          v-if="showFooter" 
          class="survey-footer"
          :class="{ 'has-saved-indicator': showSavedIndicator }"
        >
          <div v-if="showSavedIndicator" class="answers-saved-indicator">
            <span class="answers-saved-text">Данные сохранены</span>
            <i class="pi pi-bookmark answers-saved-icon"></i>
          </div>
          <div class="footer-buttons">
            <custom-button
              v-if="showBackButton"
              @click="prevStep"
              severity="danger"
              rounded
              class="back-btn"
              label="Назад"
            />
            <custom-button
              v-if="showNextButton"
              @click="handleNext"
              class="next-btn"
              :label="nextButtonLabel"
              :loading="surveyModal.isLoading"
              :disabled="!surveyStore.validateCurrentStep()"
              rounded
            />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Dialog from "primevue/dialog";

import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";

import { useSurveyModal } from "@/features/survey/store";
import DynamicStepRenderer from "./SurveySteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const surveyStore = useSurveyModal();
const { isDevice } = useDevice()

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});
const surveyModal = computed(() => surveyStore.surveyModal);
const currentStep = computed(() => surveyStore.currentStep);
const totalSteps = computed(() => surveyStore.totalSteps);

const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((surveyModal.value.currentStepIndex + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isFirstStep = surveyModal.value.currentStepIndex === 0;
  
  // Hide back button on special steps
  if (stepType === 'choice' || stepType === 'completion') {
    return false;
  }
  
  return !isFirstStep;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isLastStep = surveyModal.value.currentStepIndex >= totalSteps.value - 1;
  
  // Hide next button on special steps
  if (stepType === 'choice' || stepType === 'thank_you' || stepType === 'completion') {
    return false;
  }
  
  return !isLastStep;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === 'welcome') return "Начать тест";
  if (currentStep.value.type === 'thank_you') return "Посмотреть результаты";
  
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide footer on special steps
  return stepType !== 'choice' && stepType !== 'thank_you' && stepType !== 'completion';
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;

  // Не показываем плашку для шагов без данных или без футера
  if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
    return false;
  }

  const stepValue = surveyStore.surveyModal.surveyData[stepId];
  const hasData =
    stepValue !== undefined &&
    stepValue !== null &&
    (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

  return hasData && savedSteps.value.has(stepId);
});

function handleHideSurveyModal() {
  surveyStore.hideSurveyModal();
}

function prevStep() {
  surveyStore.prevStep();
}

function handleNext() {
  if (!currentStep.value) return;
  
  if (currentStep.value.type === 'thank_you') {
    // Submit survey and go to results
    surveyStore.submitSurvey();
  } else {
    surveyStore.nextStep();
  }
}

watch(
  () => surveyModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      savedSteps.value.clear();
    }
  }
);

// Отслеживаем изменения данных текущего шага и помечаем его как сохраненный
watch(
  () => surveyStore.surveyModal.surveyData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;

    if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
      return;
    }

    const stepValue = newData[stepId];
    const hasData =
      stepValue !== undefined &&
      stepValue !== null &&
      (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// При смене шага проверяем, есть ли уже данные, чтобы показать плашку сразу
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    const stepType = currentStep.value.type;

    if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
      return;
    }

    const stepValue = surveyStore.surveyModal.surveyData[newStepId];
    const hasData =
      stepValue !== undefined &&
      stepValue !== null &&
      (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

    if (hasData) {
      savedSteps.value.add(newStepId);
    }
  }
);
</script>

<style>
  .survey-modal-root {
    width: 60vw;
    max-width: 1020px;
    height: auto;
    max-height: 90vh;
    overflow: hidden;
  }
  
  @media (max-width: 1024px) {
    .survey-modal-root {
      width: 100%;
      height: 100%;
      max-width: 100vw;
      max-height: 100vh;
    }
  }
  </style>

<style scoped>
.survey-modal-container {
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.survey-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding: 20px 20px 0 20px;
}

.survey-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.survey-header-bottom {
  width: 100%;
}

.survey-header-left {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
}

.survey-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}

.survey-header-center {
  flex: 1;
  text-align: center;
}

.back-button {
  background: none;
  border: none;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.back-button:hover {
  background: #f5f5f7;
}

.save-exit-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  color: var(--active-100);
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-exit-button:hover {
  background: #f0f9ff;
}

.survey-title {
  display: none;
}

.progress-container {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4px;
  align-items: flex-start;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: var(--light-blue-20);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--active-100);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--active-100);
}

.survey-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  align-self: stretch;
}

.survey-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  padding: 20px 40px 40px 40px;
}

.survey-footer.has-saved-indicator {
  justify-content: space-between;
}

.footer-buttons {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.answers-saved-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 160px;
  flex-shrink: 0;
}

.answers-saved-text {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.21;
  letter-spacing: -0.04em;
  color: #6E85CB;
  white-space: nowrap;
}

.answers-saved-icon {
  font-size: 16px;
  color: var(--active-100);
  flex-shrink: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--primary-blue-80);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--grey-40);
  border-top: 3px solid var(--active-100);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .survey-modal-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }

.survey-title {
  display: block;
}


  .back-btn {
  flex: 1;
}

.next-btn {
    flex: 1;
  }

  .survey-header {
    padding: 20px 20px 0 20px;
    gap:40px;
  }

  .survey-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }


  .survey-footer {
    flex: none;
    padding: var(--Padding-XL, 20px);
    flex-direction: column;
  }

  .footer-buttons {
    width: 100%;
  }

  .survey-header-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
    white-space: nowrap;
  }

  .survey-header-right {
    display: flex;
    height: 45px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 10px;
    flex: 1 0 0;
  }

  .save-exit-button-right {
    display: none;
  }
}
</style>
